import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Button,
  Theme,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { GetApp, Delete, ChevronRight, Check, Close } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { CircularProgressOverlay } from '@campfire/circular-progress-overlay';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { TaskBadge } from '../../tasks/TaskBadge';
import {
  FormSubmissionListItem,
  FormFieldConfig,
  SortOrder,
  PAGE_SIZE,
  ExpandedFormFieldConfig,
} from './form-responses-types';
import {
  formatSubmissionDate,
  getFieldValue,
  buildExpandedColumns,
  getGroupInstanceIds,
  getGroupFieldValue,
} from './form-responses-utils';
import { SortPopover } from './SortPopover';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    sectionTitle: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: theme.color.grey.neutral400,
      letterSpacing: '-0.01em',
    },
    emptyState: {
      backgroundColor: theme.color.grey.neutralBrand100,
      borderRadius: '8px',
      padding: '48px',
      textAlign: 'center',
    },
    emptyStateTitle: {
      fontSize: '1rem',
      fontWeight: 600,
      color: theme.color.grey.neutral300,
      marginBottom: '8px',
    },
    emptyStateText: {
      fontSize: '0.875rem',
      color: theme.color.grey.neutral300,
      maxWidth: '400px',
      margin: '0 auto',
    },
    toolbarButton: {
      backgroundColor: 'transparent',
      color: '#646464',
      border: '1px solid #d9d9d9',
      borderRadius: '4px',
      padding: '4.5px 12px',
      fontSize: '12px',
      fontWeight: 600,
      textTransform: 'none',
      minWidth: 'auto',
      display: 'flex',
      alignItems: 'center',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },
    },
    dateInput: {
      color: '#646464',
      border: '1px solid #d9d9d9',
      borderRadius: '4px',
      padding: '4px 8px',
      fontSize: '12px',
      fontFamily: 'inherit',
      backgroundColor: 'transparent',
      outline: 'none',
      '&:focus': {
        borderColor: theme.palette.primary.main,
      },
    },
    dateRangeSeparator: {
      fontSize: '12px',
      color: '#646464',
    },
    tableContainer: {
      backgroundColor: '#fff',
      border: `1px solid ${theme.color.grey.neutralBrand200}`,
      borderRadius: '8px',
      overflowX: 'auto',
    },
    table: {
      minWidth: 650,
    },
    tableHeadCell: {
      backgroundColor: theme.color.grey.neutralBrand100,
      fontSize: '0.75rem',
      fontWeight: 600,
      color: theme.color.grey.neutral400,
      borderBottom: `1px solid ${theme.color.grey.neutralBrand200}`,
      whiteSpace: 'nowrap',
      padding: '12px 16px',
    },
    tableBodyRow: {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.color.grey.neutralBrand100,
      },
    },
    tableBodyCell: {
      fontSize: '0.8rem',
      color: theme.color.grey.neutral400,
      borderBottom: `1px solid ${theme.color.grey.neutralBrand200}`,
      padding: '12px 16px',
      maxWidth: '200px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    paginationContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '16px',
    },
    paginationText: {
      fontSize: '0.8rem',
      color: theme.color.grey.neutral300,
    },
    actionsColumn: {
      borderLeft: `1px solid ${theme.color.grey.neutralBrand200}`,
      position: 'sticky',
      right: 0,
      backgroundColor: '#fff',
      zIndex: 1,
    },
    actionsColumnHeader: {
      borderLeft: `1px solid ${theme.color.grey.neutralBrand200}`,
      position: 'sticky',
      right: 0,
      backgroundColor: theme.color.grey.neutralBrand100,
      zIndex: 2,
    },
    actionCell: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: '12px',
    },
    actionButton: {
      padding: '4px',
      minWidth: 'auto',
      color: '#646464',
      position: 'relative',
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: 'transparent',
        '& svg path': {
          fill: theme.palette.primary.main,
        },
      },
    },
  })
);

interface TableTabProps {
  submissions: FormSubmissionListItem[];
  formFields: FormFieldConfig[];
  listLoading: boolean;
  totalSubmissions: number;
  page: number;
  sortOrder: SortOrder;
  startDate: string | null;
  endDate: string | null;
  requiresApproval?: boolean;
  onPageChange: (page: number) => void;
  onSortChange: (order: SortOrder) => void;
  onDateRangeChange: (startDate: string | null, endDate: string | null) => void;
  onViewSubmission: (index: number, submissionId: string) => void;
  onViewComments: (index: number, submissionId: string) => void;
  onOpenWizard: (submissionId: string) => void;
  onDeleteSubmission: (submissionId: string) => void;
  onSetStatus?: (submissionId: string, status: 'approved' | 'declined' | null) => void;
  onExport: () => void;
}

export const TableTab: React.FC<TableTabProps> = ({
  submissions,
  formFields,
  listLoading,
  totalSubmissions,
  page,
  sortOrder,
  startDate,
  endDate,
  requiresApproval = false,
  onPageChange,
  onSortChange,
  onDateRangeChange,
  onViewSubmission,
  onViewComments,
  onOpenWizard,
  onDeleteSubmission,
  onSetStatus,
  onExport,
}) => {
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const totalPages = Math.ceil(totalSubmissions / PAGE_SIZE);

  const expandedColumns = React.useMemo(() => buildExpandedColumns(formFields, submissions), [formFields, submissions]);

  const groups = React.useMemo(() => {
    const map = new Map<string, Set<string>>();
    formFields.forEach((f) => {
      if (f.groupFormItemId) {
        const existing = map.get(f.groupFormItemId) || new Set<string>();
        existing.add(f.taskFieldId);
        map.set(f.groupFormItemId, existing);
      }
    });
    return map;
  }, [formFields]);

  const getCellValue = React.useCallback(
    (submission: FormSubmissionListItem, col: ExpandedFormFieldConfig) => {
      if (!col.groupFormItemId) {
        return getFieldValue(submission, col.taskFieldId);
      }
      const fieldIds = groups.get(col.groupFormItemId);
      if (!fieldIds) return '-';
      const instances = getGroupInstanceIds(submission, fieldIds);
      const instanceId = instances[col.instanceIndex];
      if (!instanceId) return '-';
      return getGroupFieldValue(submission, col.taskFieldId, instanceId);
    },
    [groups]
  );

  return (
    <Box>
      <Box display='flex' justifyContent='space-between' alignItems='center' marginBottom={3}>
        <Typography className={classes.sectionTitle}>All Responses</Typography>
        <Box display='flex' alignItems='center' style={{ gap: '8px' }}>
          <Box display='flex' alignItems='center' style={{ gap: '6px' }}>
            <input
              type='date'
              aria-label='Submissions from date'
              className={classes.dateInput}
              value={startDate ?? ''}
              max={endDate ?? undefined}
              onChange={(e) => onDateRangeChange(e.target.value || null, endDate)}
            />
            <Typography className={classes.dateRangeSeparator}>–</Typography>
            <input
              type='date'
              aria-label='Submissions to date'
              className={classes.dateInput}
              value={endDate ?? ''}
              min={startDate ?? undefined}
              onChange={(e) => onDateRangeChange(startDate, e.target.value || null)}
            />
            {(startDate || endDate) && (
              <Button className={classes.toolbarButton} onClick={() => onDateRangeChange(null, null)}>
                Clear
              </Button>
            )}
          </Box>
          <SortPopover sortOrder={sortOrder} onSortChange={onSortChange} />
          <Button
            startIcon={<GetApp style={{ fontSize: '14px' }} />}
            className={classes.toolbarButton}
            onClick={onExport}
          >
            Export
          </Button>
        </Box>
      </Box>

      {listLoading ? (
        <CircularProgressOverlay isLoading />
      ) : submissions.length === 0 ? (
        <Box className={classes.emptyState}>
          {startDate || endDate ? (
            <>
              <Typography className={classes.emptyStateTitle}>No responses in this date range</Typography>
              <Typography className={classes.emptyStateText}>
                Try widening the date range or clearing the filter to see all responses.
              </Typography>
            </>
          ) : (
            <>
              <Typography className={classes.emptyStateTitle}>No responses yet</Typography>
              <Typography className={classes.emptyStateText}>
                Share your form to start collecting submissions. Responses will appear here in a list.
              </Typography>
            </>
          )}
        </Box>
      ) : (
        <Box>
          <TableContainer className={classes.tableContainer}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeadCell}>#</TableCell>
                  <TableCell className={classes.tableHeadCell}>Date</TableCell>
                  {requiresApproval && <TableCell className={classes.tableHeadCell}>Status</TableCell>}
                  {expandedColumns.map((col) => (
                    <TableCell
                      key={`${col.taskFieldId}-${col.instanceIndex}`}
                      className={classes.tableHeadCell}
                      style={col.groupFormItemId ? { whiteSpace: 'normal', minWidth: '160px' } : undefined}
                    >
                      {col.groupFormItemId ? (
                        <>
                          <span style={{ fontSize: '0.65rem', color: '#999', display: 'block', lineHeight: 1.2 }}>
                            {col.groupTitle}
                          </span>
                          <span style={{ fontWeight: 700 }}>
                            {col.groupLabel} {col.instanceIndex + 1}
                          </span>
                          <span style={{ fontWeight: 400 }}> - {col.name}</span>
                        </>
                      ) : (
                        col.expandedName
                      )}
                    </TableCell>
                  ))}
                  <TableCell className={`${classes.tableHeadCell} ${classes.actionsColumnHeader}`}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {submissions.map((submission, index) => (
                  <TableRow key={submission.formSubmissionId} className={classes.tableBodyRow}>
                    <TableCell className={classes.tableBodyCell}>{page * PAGE_SIZE + index + 1}</TableCell>
                    <TableCell className={classes.tableBodyCell}>
                      {formatSubmissionDate(submission.submissionDate)}
                    </TableCell>
                    {requiresApproval && (
                      <TableCell
                        className={classes.tableBodyCell}
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: 110 }}
                      >
                        <StatusControls
                          status={(submission as any).status ?? null}
                          onSet={(next) => onSetStatus && onSetStatus(submission.formSubmissionId, next)}
                        />
                      </TableCell>
                    )}
                    {expandedColumns.map((col) => (
                      <TableCell
                        key={`${col.taskFieldId}-${col.instanceIndex}`}
                        className={classes.tableBodyCell}
                        style={col.groupFormItemId ? { minWidth: '160px' } : undefined}
                      >
                        {getCellValue(submission, col)}
                      </TableCell>
                    ))}
                    <TableCell className={`${classes.tableBodyCell} ${classes.actionsColumn}`}>
                      <Box className={classes.actionCell}>
                        <Box style={{ position: 'relative', display: 'inline-flex' }}>
                          <Button
                            className={classes.actionButton}
                            title='Form Wizard'
                            onClick={() => onOpenWizard(submission.formSubmissionId)}
                          >
                            <svg
                              width='18'
                              height='18'
                              viewBox='0 0 24 24'
                              fill='none'
                              xmlns='http://www.w3.org/2000/svg'
                            >
                              <path
                                d='M7.5 5.6L10 7L8.6 4.5L10 2L7.5 3.4L5 2L6.4 4.5L5 7L7.5 5.6ZM19.5 15.4L17 14L18.4 16.5L17 19L19.5 17.6L22 19L20.6 16.5L22 14L19.5 15.4ZM22 2L19.5 3.4L17 2L18.4 4.5L17 7L19.5 5.6L22 7L20.6 4.5L22 2ZM14.37 7.29C13.98 6.9 13.35 6.9 12.96 7.29L1.29 18.96C0.9 19.35 0.9 19.98 1.29 20.37L3.63 22.71C4.02 23.1 4.65 23.1 5.04 22.71L16.71 11.04C17.1 10.65 17.1 10.02 16.71 9.63L14.37 7.29Z'
                                fill='currentColor'
                              />
                            </svg>
                          </Button>
                          {submission.creationCount >= 1 && (
                            <Box style={{ position: 'absolute', bottom: 0, right: -1 }}>
                              <TaskBadge number={submission.creationCount} variant='primary' size='small' />
                            </Box>
                          )}
                        </Box>
                        <Box style={{ position: 'relative', display: 'inline-flex' }}>
                          <Button
                            className={classes.actionButton}
                            title='Comments'
                            onClick={() => onViewComments(index, submission.formSubmissionId)}
                          >
                            <svg
                              width='19'
                              height='18'
                              viewBox='0 0 19 18'
                              fill='none'
                              xmlns='http://www.w3.org/2000/svg'
                            >
                              <path
                                d='M9.17279 0C14.2387 0 18.3456 3.31835 18.3456 7.41177C18.3456 9.72424 17.0339 11.7889 14.9802 13.1479C15.2472 14.3704 15.8694 16.1836 17.3264 18C14.3921 17.4918 12.5158 16.0089 11.3559 14.6033C10.6547 14.742 9.92649 14.8235 9.17279 14.8235C4.10686 14.8235 0 11.5052 0 7.41177C0 3.31835 4.10686 0 9.17279 0Z'
                                fill='#646464'
                              />
                            </svg>
                          </Button>
                          {submission.commentCount >= 1 && (
                            <Box style={{ position: 'absolute', top: 0, right: -1 }}>
                              <TaskBadge number={submission.commentCount} variant='warning' size='small' />
                            </Box>
                          )}
                        </Box>
                        <Button
                          className={classes.actionButton}
                          title='Delete'
                          onClick={() => onDeleteSubmission(submission.formSubmissionId)}
                        >
                          <Delete style={{ fontSize: '18px' }} />
                        </Button>
                        <Button
                          className={classes.actionButton}
                          title='View'
                          onClick={() => onViewSubmission(index, submission.formSubmissionId)}
                        >
                          <ChevronRight style={{ fontSize: '20px' }} />
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box className={classes.paginationContainer}>
              <Typography className={classes.paginationText}>
                Showing {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, totalSubmissions)} of{' '}
                {totalSubmissions}
              </Typography>
              <Box display='flex' style={{ gap: '8px' }}>
                <TabletButton
                  variant='outlined'
                  size='small'
                  disabled={page === 0}
                  onClick={() => onPageChange(page - 1)}
                  style={{
                    borderColor: theme.color.grey.neutralBrand300,
                    color: theme.color.grey.neutral400,
                    textTransform: 'none',
                  }}
                >
                  Previous
                </TabletButton>
                <TabletButton
                  variant='outlined'
                  size='small'
                  disabled={page >= totalPages - 1}
                  onClick={() => onPageChange(page + 1)}
                  style={{
                    borderColor: theme.color.grey.neutralBrand300,
                    color: theme.color.grey.neutral400,
                    textTransform: 'none',
                  }}
                >
                  Next
                </TabletButton>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

interface StatusControlsProps {
  status: 'approved' | 'declined' | null;
  onSet: (next: 'approved' | 'declined' | null) => void;
}

const StatusControls: React.FC<StatusControlsProps> = ({ status, onSet }) => {
  const approvedColor = '#00b16a';
  const declinedColor = '#d0453c';
  const pendingColor = '#bdbdbd';

  const toggle = (target: 'approved' | 'declined') => {
    onSet(status === target ? null : target);
  };

  return (
    <Box display='flex' alignItems='center' style={{ gap: 4 }}>
      <Button
        title={status === 'approved' ? 'Approved — click to clear' : 'Approve'}
        onClick={() => toggle('approved')}
        style={{
          minWidth: 28,
          width: 28,
          height: 28,
          padding: 0,
          borderRadius: 4,
          backgroundColor: status === 'approved' ? approvedColor : 'transparent',
          border: `1px solid ${status === 'approved' ? approvedColor : '#e0e0e0'}`,
        }}
      >
        <Check style={{ fontSize: 16, color: status === 'approved' ? '#fff' : pendingColor }} />
      </Button>
      <Button
        title={status === 'declined' ? 'Declined — click to clear' : 'Decline'}
        onClick={() => toggle('declined')}
        style={{
          minWidth: 28,
          width: 28,
          height: 28,
          padding: 0,
          borderRadius: 4,
          backgroundColor: status === 'declined' ? declinedColor : 'transparent',
          border: `1px solid ${status === 'declined' ? declinedColor : '#e0e0e0'}`,
        }}
      >
        <Close style={{ fontSize: 16, color: status === 'declined' ? '#fff' : pendingColor }} />
      </Button>
    </Box>
  );
};
